import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { FormControl, FormRecord } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { FilterSpecification } from 'maplibre-gl';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    EntityTableDateCellComponent,
    EntityTableDateCellContext,
} from '@connect-services/client/components/entity-table-date-cell/entity-table-date-cell.component';
import { EntityTableDateHourCellComponent } from '@connect-services/client/components/entity-table-date-hour-cell/entity-table-date-hour-cell.component';
import { MapBaseSource } from '@connect-services/client/components/map/map.component';
import { AttributeValue } from '@connect-services/client/components/table-entity-detail/abstract-entity-detail.component';
import {
    BroadcastChannelEvent,
    BroadcastChannelMessageEnum,
    BroadcastChannelNameEnum,
    BroadcastChannelSourceEnum,
} from '@connect-services/client/models/broadcast-channel-data.model';
import { AssertService } from '@connect-services/client/services/assert.service';
import { AttributeInformationContextService } from '@connect-services/client/services/attribute-information-context.service';
import { CustomDateAdapterService, DayOfWeekEnum } from '@connect-services/client/services/custom-date-adapter.service';
import { EditEntityDetailService } from '@connect-services/client/services/edit-entity-detail.service';
import { EditModeStateService } from '@connect-services/client/services/edit-mode-state.service';
import { EntityDetailCreationContextService } from '@connect-services/client/services/entity-detail-creation-context.service';
import { HomeService } from '@connect-services/client/services/home.service';
import { MapEditGeomService } from '@connect-services/client/services/map-edit-geom.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import {
    ADAPTER_DATE_FORMATS,
    EN_DATE_FORMAT,
    TranslateService,
} from '@connect-services/client/services/translate.service';
import { InputNumberComponent } from '@connect-services/src/lib/components/input/input-number/input-number.component';
import { InputTextComponent } from '@connect-services/src/lib/components/input/text/input-text/input-text.component';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import {
    InformationSchemaTableExtended,
    MapboxStyle,
    StandardizedAttributeResponse,
} from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { defaultPaginationParameters } from '@shared/client/models/pagination.model';

export enum EntityAttributesInputType {
    BOOLEAN = 'BOOLEAN',
    DATE = 'DATE',
    NUMBER = 'NUMBER',
    STRING = 'STRING',
    TIMESTAMPTZ = 'TIMESTAMPTZ',
}

export type AttributeValues = {
    errorKey: StandardizedAttributeResponse.ErrorKeyEnum | null;
    hasNextPage: boolean;
    values: string[];
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        EntityTableDateCellComponent,
        EntityTableDateHourCellComponent,
        InputNumberComponent,
        InputTextComponent,
        RessourcesModule,
        SelectComponent,
    ],
    providers: [
        { deps: [TranslateService], provide: DateAdapter, useClass: CustomDateAdapterService },
        { provide: MAT_DATE_FORMATS, useValue: ADAPTER_DATE_FORMATS },
    ],
    selector: 'app-entity-attributes-input',
    templateUrl: './entity-attributes-input.component.html',
})
export class EntityAttributesInputComponent {
    public readonly attribute = input.required<AttributeValue>();
    public readonly attributesForm = input.required<FormRecord<FormControl<unknown>>>();
    public readonly columnNamesWithReference = input<string[]>([]);
    public readonly disabled = input(false);
    public readonly informationSchemaTable = input.required<InformationSchemaTableExtended | undefined>();
    public readonly label = input<string | undefined>();
    public readonly labelTooltip = input<string | undefined>();
    public readonly tooltip = input<string | undefined>(undefined);
    public readonly tooltipValue = signal<string | undefined>(undefined);

    protected readonly attributeInfo = computed(() => {
        return this.attributeInformationContext.getAttributeInformation(this.attribute().key);
    });
    protected readonly attributeOptions = signal<SelectOption<string>[]>([]);
    protected readonly control = computed<FormControl<unknown> | null>(() =>
        this.getControl(this.attribute(), this.attributesForm()),
    );
    protected dateFormatPlaceHolder = EN_DATE_FORMAT;
    protected defaultAttributeOption: SelectOption<unknown>[] = [];
    protected defaultOptionValue: SelectOption<unknown>[] = [];
    protected readonly errorMessages = signal<string[]>([]);
    protected isAttributeValuesAlreadyFetch = false;
    protected readonly isReferredLayerNotFound = signal<boolean>(false);
    protected readonly isValueEdited = computed<boolean>(() => {
        // called to trigger computed on user changes to always have the good state
        this.userChangesCount();

        // cannot use computed control because it might not call this method if control object reference doesn't change
        return this.editEntityDetailService.isValueEdited(
            this.attribute().key,
            this.getControl(this.attribute(), this.attributesForm())?.value,
        );
    });
    protected readonly optionLoading = signal<boolean>(false);
    protected options: SelectOption<unknown>[] = [
        { label: 'TRUE', value: true },
        { label: 'FALSE', value: false },
    ];
    protected readonly paginationParameters = { ...defaultPaginationParameters, take: 20 };
    protected readonly selectedAttributeOption = signal<SelectOption<unknown>[]>([]);
    protected readonly String = String;
    protected readonly type = computed(() => {
        switch (this.informationSchemaTable()?.dataType) {
            case InformationSchemaTableExtended.DataTypeEnum.REAL:
            case InformationSchemaTableExtended.DataTypeEnum.INTEGER:
            case InformationSchemaTableExtended.DataTypeEnum.FLOAT:
                return EntityAttributesInputType.NUMBER;
            case InformationSchemaTableExtended.DataTypeEnum.BOOLEAN:
                return EntityAttributesInputType.BOOLEAN;
            case InformationSchemaTableExtended.DataTypeEnum.DATE:
                return EntityAttributesInputType.DATE;
            case InformationSchemaTableExtended.DataTypeEnum.TIMESTAMPTZ:
                return EntityAttributesInputType.TIMESTAMPTZ;
            default:
                return EntityAttributesInputType.STRING;
        }
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly editEntityDetailService = inject(EditEntityDetailService);
    protected EntityAttributesInputType = EntityAttributesInputType;
    protected readonly EntityTableDateCellContext = EntityTableDateCellContext;

    private broadcastChannel = new BroadcastChannel(BroadcastChannelNameEnum.LAYERS);
    private hasNextPage: boolean = false;
    private nextPage = 1;
    private searchValue: string | undefined = undefined;
    private readonly userChangesCount = signal<number>(0);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly attributeInformationContext = inject(AttributeInformationContextService);
    private dateAdapter = inject(DateAdapter<Date>);
    private readonly editModeStateService = inject(EditModeStateService);
    private readonly entityDetailCreationContextService = inject(EntityDetailCreationContextService);
    private readonly homeService = inject(HomeService);
    private readonly mapEditGeomService = inject(MapEditGeomService);
    private readonly mapEditModeService = inject(MapEditModeService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly sourcesService = inject(SourcesService);
    private readonly translateService = inject(TranslateService);

    public constructor() {
        this.dateFormatPlaceHolder = this.translateService.getDateFormat();
        this.dateAdapter.getFirstDayOfWeek = (): number => DayOfWeekEnum.MONDAY;

        effect(() => {
            this.editModeStateService.getStateTick();
            const project = this.projectBloc.value();
            const layer = this.editEntityDetailService.layer();

            if (!project || !layer || this.mapEditModeService.activeGeometryType()) {
                return;
            }

            untracked(async () => {
                await this.handleStateChange(project.id, layer.id);
            });
        });

        effect(() => {
            const attributes = this.attributesForm();
            const information = this.informationSchemaTable();

            untracked(() => this.refreshControlState(attributes, information));
        });

        effect(() => {
            if (!this.tooltip()) {
                return;
            }

            this.tooltipValue.set(this.tooltip());
        });
    }

    public getTooltipValue(): string | undefined {
        if (this.tooltip()) {
            return this.tooltip();
        }

        const control = this.attributesForm().controls[this.attribute().key];
        if (!control) {
            return undefined;
        }

        const value = control.value;
        if (value == null || value === '') {
            return undefined;
        }

        return String(value);
    }

    public resetStandardizedAttributeValue(): void {
        this.setValueAndErrorsMessage(null);

        if (this.errorMessages().length) {
            return;
        }

        this.selectedAttributeOption.set([]);
    }

    public selectBooleanOption(value: unknown[]): void {
        this.setValueAndErrorsMessage(value[0] ?? null);
    }

    public setStandardizedAttributesValue(value: unknown): void {
        if (!value) {
            return;
        }

        this.setValueAndErrorsMessage(value ?? null);

        if (this.errorMessages().length) {
            return;
        }

        this.setSelectedAttributeOption(value ?? null);
    }

    public setValueAndErrorsMessage(value?: unknown, isUserChange: boolean = true): void {
        const key = this.attribute().key;
        const control = this.attributesForm().controls[key];

        if (!control) {
            return;
        }

        control.setValue(value ?? null);

        if (isUserChange) {
            this.userChangesCount.update((value) => value + 1);
            control.markAsDirty();
            control.markAsTouched();
            this.editEntityDetailService.after.set(key, value ?? null);
            this.editEntityDetailService.refreshDirtyAttributesState();
        }

        this.tooltipValue.set(this.getTooltipValue());

        if (this.disabled()) {
            return;
        }

        this.errorMessages.set(this.editEntityDetailService.getFormErrors(control));
    }

    protected async fetchNextPage(): Promise<void> {
        if (!this.hasNextPage) {
            return;
        }

        const attributeValues = await this.fetchAttributeValues(this.nextPage, this.searchValue);

        if (!attributeValues) {
            return;
        }

        this.nextPage = this.nextPage + 1;

        this.attributeOptions.update((currentAttributeValues) => {
            return [
                ...currentAttributeValues,
                ...attributeValues.values.map((label: string) => {
                    return { label, value: label };
                }),
            ];
        });
    }

    protected async initAttributeValues(): Promise<void> {
        if (this.isAttributeValuesAlreadyFetch) {
            return;
        }

        this.isAttributeValuesAlreadyFetch = true;

        await this.setAttributeValuesFirstPage();
    }

    protected async search(value: string): Promise<void> {
        this.searchValue = value;
        this.nextPage = 1;

        await this.setAttributeValuesFirstPage();
    }

    private async fetchAttributeValues(page: number, searchValue?: string): Promise<AttributeValues | null> {
        const layer = this.editEntityDetailService.layer();

        if (!layer) {
            return null;
        }

        this.optionLoading.set(true);

        const attributeValues = await this.editEntityDetailService.getAttributesValue(
            this.attribute().key,
            layer,
            page,
            searchValue,
        );
        this.hasNextPage = attributeValues.hasNextPage;
        this.isReferredLayerNotFound.set(
            attributeValues.errorKey === StandardizedAttributeResponse.ErrorKeyEnum.REFERRED_LAYER_NOT_FOUND,
        );
        this.optionLoading.set(false);

        return attributeValues;
    }

    private flattenFilters(input: (unknown | unknown[])[]): unknown[] {
        return input.reduce<unknown[]>((acc, item) => {
            if (Array.isArray(item)) {
                return acc.concat(this.flattenFilters(item));
            }

            return acc.concat(item);
        }, []);
    }

    private getControl(
        attribute: AttributeValue,
        attributesForm: FormRecord<FormControl<unknown>> | null,
    ): FormControl<unknown> | null {
        return attributesForm?.controls?.[attribute.key] ?? null;
    }

    private getFilterAndLayoutKeys(source: MapBaseSource<unknown>, filters: FilterSpecification[]): unknown[] {
        const styleLabel = source.style.find((style) => style.id === 'label');
        const filterFlatKeys = filters.flatMap((item) => (Array.isArray(item) ? this.flattenFilters(item) : item));

        if (styleLabel?.layout && styleLabel.type === MapboxStyle.TypeEnum.SYMBOL) {
            AssertService.isSymbolLayerSpecification(styleLabel);
            const labelKeys = Array.isArray(styleLabel.layout['text-field'])
                ? styleLabel.layout['text-field']
                : [styleLabel.layout['text-field']];
            filterFlatKeys.push(...labelKeys);
        }

        return filterFlatKeys;
    }

    private async handleStateChange(projectId: string, layerId: string): Promise<void> {
        const attributeKey = this.attribute().key;

        const edited = this.editModeStateService.getEditedProperty(
            projectId,
            layerId,
            this.editEntityDetailService.entityId(),
            attributeKey,
        );

        if (
            edited === undefined &&
            this.entityDetailCreationContextService.entityId() === this.editEntityDetailService.entityId()
        ) {
            return;
        }

        const newValue = edited !== undefined ? edited : this.attribute().value;

        if (this.columnNamesWithReference().includes(attributeKey)) {
            this.setValueAndErrorsMessage(newValue, false);

            if (this.errorMessages().length) {
                return;
            }

            this.setSelectedAttributeOption(newValue);
            await this.redrawSourceIfNeeded();

            return;
        }

        this.setValueAndErrorsMessage(newValue, false);
        await this.redrawSourceIfNeeded();
    }

    private async redrawSourceIfNeeded(): Promise<void> {
        const source = this.sourcesService
            .sources()
            .find((source) => source.sourceId === this.editEntityDetailService.layer()?.id);

        if (!source) {
            return;
        }

        const filters: FilterSpecification[] = source.style
            .flatMap((style) => style.filter as FilterSpecification[] | undefined)
            .filter((filter): filter is FilterSpecification => !!filter);

        if (!filters.length) {
            return;
        }

        const attributeKeys = [this.attribute().key];

        if (this.editEntityDetailService.businessIdentifierKey() === this.attribute().key) {
            attributeKeys.push('businessIdentifier');
        }

        const filterFlatKeys = this.getFilterAndLayoutKeys(source, filters);

        if (attributeKeys.every((attributeKey) => !filterFlatKeys.includes(attributeKey))) {
            return;
        }

        if (this.homeService.isExternalPage()) {
            this.broadcastChannel.postMessage(<BroadcastChannelEvent<MapBaseSource<unknown>>>{
                additionalData: source,
                message: BroadcastChannelMessageEnum.REDRAW_SOURCE,
                source: BroadcastChannelSourceEnum.ENTITY_DETAIL,
            });

            return;
        }

        await this.mapEditGeomService.drawSource(source);
    }

    private refreshControlState(
        attributes: FormRecord<FormControl<unknown>>,
        information: InformationSchemaTableExtended | undefined,
    ): void {
        const attributeControl = this.getControl(this.attribute(), attributes);

        if (!attributeControl) {
            return;
        }

        this.tooltipValue.set(this.getTooltipValue());
        this.errorMessages.set(this.editEntityDetailService.getFormErrors(attributeControl));

        this.setSelectedAttributeOption(attributeControl.value);
        this.defaultAttributeOption = this.selectedAttributeOption();

        if (information?.dataType !== InformationSchemaTableExtended.DataTypeEnum.BOOLEAN) {
            return;
        }

        const value = attributeControl.value;

        this.defaultOptionValue =
            value === null || value === undefined ? [] : [{ label: String(value).toUpperCase(), value }];
    }

    private async setAttributeValuesFirstPage(): Promise<void> {
        const attributeValues = await this.fetchAttributeValues(0, this.searchValue);

        if (!attributeValues) {
            return;
        }

        this.attributeOptions.set(
            attributeValues.values.map((label: string) => {
                return { label, value: label };
            }),
        );
    }

    private setSelectedAttributeOption(value?: unknown): void {
        if (!value) {
            this.selectedAttributeOption.set([]);

            return;
        }

        this.selectedAttributeOption.set([{ label: String(value), value }]);
    }
}
